/**
 * Per-page document metadata for the SPA.
 *
 * Replaces the framework-managed <head> that came with server rendering. Tags
 * are written on mount and reverted on unmount, so navigating away from a page
 * that set `robots: noindex` does not leave that tag behind.
 *
 * Note this runs client-side: crawlers that do not execute JavaScript see only
 * the defaults in index.html. That is the accepted trade of a client-rendered
 * app, and every page here except the public welcome screen sits behind a login
 * anyway.
 */

import { useEffect } from "react";

export interface PageMeta {
  title: string;
  description?: string;
  /** Set to "noindex" for internal screens such as the admin portal. */
  robots?: string;
}

function setTag(selector: string, attrs: Record<string, string>): () => void {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) {
    const previous = existing.getAttribute("content");
    existing.setAttribute("content", attrs["content"] ?? "");
    return () => {
      if (previous === null) existing.removeAttribute("content");
      else existing.setAttribute("content", previous);
    };
  }

  const created = document.createElement("meta");
  for (const [key, value] of Object.entries(attrs)) created.setAttribute(key, value);
  document.head.appendChild(created);
  return () => created.remove();
}

export function usePageMeta({ title, description, robots }: PageMeta): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const cleanups = [
      setTag('meta[property="og:title"]', { property: "og:title", content: title }),
      ...(description
        ? [
            setTag('meta[name="description"]', { name: "description", content: description }),
            setTag('meta[property="og:description"]', {
              property: "og:description",
              content: description,
            }),
          ]
        : []),
      ...(robots ? [setTag('meta[name="robots"]', { name: "robots", content: robots })] : []),
    ];

    return () => {
      document.title = previousTitle;
      for (const cleanup of cleanups) cleanup();
    };
  }, [title, description, robots]);
}
